
import {useRef, useState, useImperativeHandle, forwardRef, useLayoutEffect} from 'react';
import './PaymentActionBox.less';
import paymentApi from '../paymentApi';
import Transaction from '../../../entities/transaction';
import util from '../../../lib/util';
import IconButton from '../../../ui/IconButton/IconButton';
import Icons from '../../../ui/IconButton/Icons';
import OptionHelper from '../../../Configuration/GeneralOptions/OptionHelper';
import {connect} from 'react-redux';
import {sendEmails} from '../../../rest/api';
import Lead from '../../../entities/lead';
import Matter from '../../../entities/matter';
import i18n from '../../../i18n/i18n';
import {formatFML, formatFMLFromId} from "../../../Contacts/ContactFormat";
import find from "../../../store/find";
import {getEmails} from "../../../lib/picos/getResourceTypes";
import {getCurrentFirm, getCurrentFirmId, isPortalUser} from "../../../lib/picos/currentLogin";
import RoutedIconButton from "../../../ui/Buttons/RoutedIconButton";
import * as TransactionActions from "../../../store/actions/transactionActions";
import CopyText from "../../../ui/CopyText";
import {getUserWhoProcessedTransaction} from "../../../rest/payrix";
import Alert from "../../../lib/alert";
import Contact from "../../../entities/contact";
import type {RootDispatch, RootState} from "../../../@types/store";
import type {PayrixPaymentMeta, PayrixTransactionFull} from "../../../@types/payrix";
import {ETransactionStatusName, ETransactionType, ZPayrixPaymentMeta} from "../../../@types/payrix";
import {isLawpayFormat, normalizePaymentMeta} from "../normalizePaymentMeta";
import {classifyPaymentForEmail} from "../paymentEmailClassify";
import {isCreditCard, TxnResultTypes} from "../Payrix/Payrix";
import {chargeToStore} from "../Payrix/refundCharge";
import {refundKeyFor, refundSucceeded, type RefundSubmission} from "../refundKeys";
import type {EmailOptions, PaymentEmails} from "../../../@types/email";
import {ZEmailOptions, ZPaymentEmails} from "../../../@types/email";
import {existsAndNotDeletedFilter, existsFilter} from "../../../lib/picos/filters";
import type {PaymentMethod, ProcessorPaymentMeta} from "../../../@types/payments";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {
    PaymentAccountType,
    PaymentChargedOn,
    PaymentConsultName,
    PaymentDeposited,
    PaymentForCase,
    PaymentFund,
    PaymentHtmlSubject,
    PaymentNotes,
    PaymentPayerName,
    PaymentRefundCharge,
    PaymentRefunded,
    PaymentReplenish,
    PaymentTxDate,
    PaymentVoidedCharge
} from "../../../i18n/transaction";
import Loading from "../../../ui/Loading";
import useComponentCache from "../../../hooks/useComponentCache";
import {runInFlight, useInFlight} from "../../../hooks/useInFlight";
import {
    chargeOutcomeUnknown,
    chargeUnknownMessage,
    PaymentNotSentError,
    PaymentOutcomeUnknownError,
    PortalChargeOperatorError,
    PortalChargeRefusedError,
} from "../paymentOutcome";
import {
    adoptPortalTransaction,
    forgetPortalCharge,
    pendingPortalCharge,
    portalChargeBusyMessage,
    portalChargeFirmNotLoadedMessage,
    portalChargeLocksAvailable,
    portalChargeNoCaseMessage,
    portalChargeNoRecordMessage,
    portalChargeOldBrowserMessage,
    portalChargeOperatorMessage,
    portalChargeRefusedMessage,
    portalChargeSignInMessage,
    portalChargeStuckMessage,
    portalChargeUnavailableMessage,
    portalChargeUnknownMessage,
    refreshPortalTransaction,
    rememberPortalCharge,
    withPortalChargeLock,
    type PortalChargeAttempt,
} from "../portalCharge";
import {firmCopy} from "../../../Configuration/FirmEmail/firmEmailCopies";

export type PaymentActionBoxProps = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> &
    {
        // How much the charge is/was for
        transaction: Transaction,

        // Payment Method
        paymentMethod?: PaymentMethod,

        // Which target account (processor's accounts) this transaction is for
        accountId: string,

        // If this is associated with a lead, what is the lead
        lead?: Lead,

        // Is this the lead's paid initial consult? Only then does the receipt say the funds paid
        // for the consult. (OWA-957)
        isConsultPayment?: boolean,

        // If this is a case, what is the matter
        matter?: Matter,

        // After a charge method is called, send the metadata back to the parent
        onChange?: Function,

        // Call after a successful processing of a payment has been made
        onProcess?: Function,

        // Called if the payment was refused via LawPay
        onRefused?: Function,

        // Called when a refund is processed
        onRefunded?: Function,

        // Called to restore the payment
        onRestored?: Function,

        // Called if the payment was voided via LawPay
        onVoided?: Function,
    };

export type PaymentActionBoxState = {
    errOccurred: boolean,
    errMessage: string,
    userProcessor: string,
}

export type PaymentActionBoxHandle = {
    onParentClose: () => void;
}

/**
 * This widget handles credit card and eCheck processing.  It has three methods that it calls on the server:
 * createCharge, voidCharge and refundCharge.  The props for this widget contain all of the information required.
 *
 * After any of these methods returns, we need to send three pieces of information back to the parent using the
 * onChange callback:
 * - processor: Which payment processor handled this transaction?  Get this from paymentApi.getName.
 * - processorPaymentId: The payment ID passed to us from the processor, e.g. pmtd_WesqHDqMXBn1RoI6qDTOu
 * - processorPaymentMeta: Any metadata passed from the processor along with the transaction (json)
 */
const PaymentActionBox = forwardRef<PaymentActionBoxHandle, PaymentActionBoxProps>((props, ref) => {
    const key = `payment-action-box-${props.transaction?.id || ''}`;
    const {cacheReset, cacheSave, cacheValue} = useComponentCache<PaymentActionBoxState>(key);

    const doResetRef = useRef<boolean>(false);

    const [errOccurred, setErrOccurred] = useState<boolean>(cacheValue('errOccurred', false));
    const [errMessage, setErrMessage] = useState<string>(cacheValue('errMessage', ''));
    const [userProcessor, setUserProcessor] = useState<string>(cacheValue('userProcessor', ''));
    // OWA-1342: a processor call outlives this component, so its in-flight flag lives in a
    // module-level registry, not in cached state: a remount mid-call stays disabled, and the
    // flag clears when the call settles even if this box has unmounted.
    const flightKey = `payment-action-${props.transaction?.id || ''}`;
    const isTxnProcessing = useInFlight(flightKey);
    // Not cached: a Payrix response has no Redux counterpart to refresh it, and txnQuery
    // refetches the status on mount (OWA-722).
    const [txn, setTxn] = useState<PayrixTransactionFull>(null);

    useImperativeHandle(ref, () => ({
        onParentClose: () => {
            doResetRef.current = true;
        }
    }), []);

    useLayoutEffect(() => {
        return () => {
            // Read at cleanup: onParentClose sets the flag and the box unmounts before this
            // effect runs again, so a value captured in the effect body missed it.
            if (doResetRef.current) cacheReset();
            else cacheSave({errOccurred, errMessage, userProcessor});
        }
    }, [cacheReset, cacheSave, props.transaction, userProcessor, errOccurred, errMessage]);

    const fetchTxn = async (): Promise<PayrixTransactionFull> => {
        // Get any updates to the status of this transaction
        if (props.transaction.processorPaymentMeta?.id && paymentApi.isPayrix() && paymentApi.isConnected()) {
            let _txn = await paymentApi.getTransaction(props.transaction.processorPaymentMeta.id);

            // OWA-978: _txn is shown as is (after a refund it is the refund), but the payment stores
            // its charge. With no charge to store, the stored metadata stays as it is.
            const charge = chargeToStore(_txn);

            // OWA-595: pass the local Transaction (not the PaymentMethod) — the Payrix
            // delegate uses it to rebuild a missing description (id/isTrust/notes) for
            // legacy txns. _txn is the Payrix txn for props.transaction.
            const meta = charge && paymentApi.parsePaymentMeta(charge, props.transaction);

            // Backfill voidedDate when the processor reports a past void/refund
            // the transaction hasn't recorded yet. These three writers are NOT
            // redundant — each covers a role the others don't, so do not "clean
            // up" one without checking the quadrant matrix in OWA-570:
            //   - onSaveTransactionQueue(tr): persists voidedDate via the queue.
            //   - onRefunded(meta) → PaymentCard.onPaymentRefunded → onChange(clone)
            //     (no save flag): the ONLY thing that updates the DISPLAYED txn on
            //     this path (TransactionCard.onCardChange ignores onChange(_, true)).
            //   - the onChange(meta) below → onPaymentActionChange persists via
            //     dispatch and updates other status fields, but is display-inert.
            // The !voidedDate guard here is deliberate: onPaymentActionChange's
            // deepEqual(meta) guard skips the unchanged-meta case this must catch.
            if (meta?.voidOrRefundDate && !props.transaction.voidedDate) {
                // OWA-1454: the portal never saves a payment row; it reads the server's row instead
                if (isPortalUser()) void props.onRefreshPortalTransaction(props.transaction.id).catch(console.error);
                else {
                    const tr = props.transaction.clone();
                    tr.voidedDate = meta.voidOrRefundDate;
                    props.onSaveTransactionQueue(tr);
                }
                props.onRefunded?.(meta);
            }

            try {
                let userName = await getUserWhoProcessedTransaction(props.transaction);
                setUserProcessor(userName);
            }
            catch (err) {
                console.error('Error fetching user who processed transaction:', err);
            }

            if (meta && props.onChange) props.onChange(meta);

            return _txn;
        }
        else return null;
    }

    const txnQuery = useQuery({
        queryKey: [key, props.transaction?.addedOn, props.transaction?.dbAddedOn],
        queryFn: fetchTxn,
        placeholderData: keepPreviousData || null
    });

    /**
     * Get emails for each recipient.  Returns an object with keys for each email personal: payer, client(s), firm.
     * e.g. {
     *     clients: [{
     *       name: 'John Margaglione',
     *       email: 'john@margaglione.org'
     *     }],
     *     payer: {
     *       name: 'Jennifer Aniston',
     *       email: 'jennifer@aniston.pro'
     *     },
     *     firm: {
     *       name: 'Margaglione Law',
     *       email: 'me@outlaw.com'
     *     }
     * }
     *
     * @returns {PaymentEmails}
     */
    const getEmailInfo = (partial: boolean): PaymentEmails => {
        const {transaction} = props;

        const optionHelper = new OptionHelper();
        const emailPayer = optionHelper.getOptionValue(OptionHelper.EMAIL_PAYER);
        const emailClient = optionHelper.getOptionValue(OptionHelper.EMAIL_CLIENT);

        // The firm's copy is not a target: see receiptFirmCopy (OWA-1288).
        const targets: Partial<PaymentEmails> = {};

        if (emailPayer) {
            // Is there a separate payer?
            const payer = !transaction.clientIds.includes(transaction.source);
            const email = getEmails(find<Contact>('contacts', transaction.source))?.find(e => e.isPrimary)?.value;
            if (payer && email) targets.payer = {
                name: formatFMLFromId(transaction.source),
                email
            }
        }

        if (emailClient) {
            const clientEmails = transaction.clientIds
                .map(cid => find<Contact>('contacts', cid)) // contacts
                .filter(existsFilter)
                .map(c => {
                    const email = getEmails(c).find(e => e.isPrimary);

                    if (email) return {
                        name: formatFML(c),
                        email: email.value
                    }
                    else return null;
                    // }
                }) // emails
                .filter(util.existsFilter)

            if (clientEmails.length > 0) targets.clients = clientEmails;
        }

        const parsedTargets = partial ? ZPaymentEmails.partial().safeParse(targets) : ZPaymentEmails.safeParse(targets);
        if (parsedTargets.error) console.error('Failed parsing ZEmailInfo', parsedTargets, targets);

        return targets as PaymentEmails;
    }

    /** The firm's copy of a receipt and its Reply-To (OWA-1288). */
    const receiptFirmCopy = () =>
        firmCopy(getCurrentFirm(), 'payments', !!new OptionHelper().getOptionValue(OptionHelper.EMAIL_SELF));

    /**
     * Send emails to the payer, client and firm (if requested).
     *
     * @param {ProcessorPaymentMeta} meta - parsed metadata
     * @returns {Promise<string>}
     */
    const _sendEmails = async (meta: ProcessorPaymentMeta): Promise<string> => {
        const parseResult = ZPayrixPaymentMeta.safeParse(meta);
        if (parseResult.error) {
            console.error(parseResult.error, meta);
            Alert.addSimpleError(parseResult.error.message);
        }

        // For easier access to props
        const {transaction, matter, lead} = props;
        const isTrustRelated = transaction.txType === 'FR';
        // Replenishing means an earlier deposit reached the trust this one targets: the same target (a client's
        // general trust or a case trust), or, for a split deposit, a positive amount allocated to this case trust.
        // Voided and refused deposits moved no money, and one dated after this charge is not earlier. Read from the
        // transactions, not the matter prop (absent when charged from a lead or a client), and not getTransactions,
        // which drops anything already on an invoice. (OWA-959)
        const isReplenishment = isTrustRelated && props.transactions
            .filter(existsAndNotDeletedFilter)
            .some(tr => tr.txType === 'FR' && tr.id !== transaction.id && !tr.voidedDate && !tr.refusedDate &&
                tr.txDate <= meta.chargeDate &&
                (tr.paymentDistribution?.length > 0
                 ? (Number.parseFloat(tr.paymentDistribution.find(pd => pd.matterId === transaction.target)?.amount ?? '') || 0) > 0
                 : tr.target === transaction.target));
        const isCaseTrust = isTrustRelated && transaction.targetType === 'M';
        // Charged from a client ledger there is no matter or lead; name the transaction's clients. (OWA-959)
        const clientNames = (matter?.clients || lead?.clients || transaction.clientIds)
            .map(cid => formatFMLFromId(cid, false));
        const firm = getCurrentFirm();
        const billingName = firm.billingName || firm.legalName;

        // Convert metadata to sane variables
        const {
            amount,
            amount_refunded,
            chargeDate,
            voidOrRefundDate,
            method,
            payment
        } = meta;

        const paymentMethod = /*payment ||*/ method;

        // A Payrix void surfaces as statusText === Voided (not a CreditCardReverseAuth type).
        // Keying off the old LawPay-era ReverseAuth check emailed clients a "Thank you for your
        // payment!" receipt when they voided a charge. See classifyPaymentForEmail. (OWA-508)
        const {isVoid, isRefund} = classifyPaymentForEmail(meta);

        // Email addresses to send to
        const emails = getEmailInfo(false); // We will send them all on the TO line.
        /* TODO: Why isn't this being used? */
        const emailAddresses = Object.values(emails).flat().map(o => o.email);

        /* Now we need to format the email message.  We will need:
           - What is the purpose of this payment?
               a. Is this a payment for an invoice? If so, which invoice (or payment for a CSM?)
               b. Is this payment to fund or replenish a trust? If so, is this a client or case trust?
               c. Was this a voided transaction?
               d. Was this a full or partial refund?  If partial, how much?
           - Which lead/case is this payment related to?  Use case name or code?  Use lead name or something else?
           - What was the amount of the transaction?
           - Name of the payer (if not the client)
           - Name of the client
           - Type of account (card or bank)
             a. If a card, what card type (e.g. Mastercard), and what is the last four of the card?
             b. If a bank, what type of account (e.g. checking), and what are the last four of the account #?
           - Notes from the transaction card.
         */

        // Here are the variables to be filled in:

        // Trust name
        const trustName = isCaseTrust ? i18n.ls('transaction', 'pmtCaseTrust')
                                      : i18n.ls('transaction', 'pmtGeneralTrust');

        const isPartial = isRefund ? amount_refunded < amount : false;
        // const partialFmt = isPartial ? util.formatCurrency(amount_refunded, true) : null;

        // Subject of the email (also the first line of the text).
        // e.g. Thank you for your payment!
        //      Thank you for funding your trust account!
        //      Thank you for replenishing your trust account!
        const subject = isVoid ? i18n.ls('transaction', 'pmtVoided')
                               : isRefund ? i18n.ls('transaction', 'pmtRefunded', {isPartial} as PaymentRefunded)
                                          : isTrustRelated
                                            ? isReplenishment
                                              ? i18n.ls('transaction', 'pmtReplenish', {trustName} as PaymentReplenish)
                                              : i18n.ls('transaction', 'pmtFund', {trustName} as PaymentFund)
                                            : i18n.ls('transaction', 'pmtThankYou');

        // e.g. 10/11/2021 at 03:21pm
        const paymentDate = i18n.ls('transaction', 'pmtTxDate', {
            txDate: chargeDate.toLocaleDateString('en-US'),
            txTime: chargeDate.toLocaleTimeString('en-US').toLowerCase()
        } as PaymentTxDate);

        const voidOrRefundOn = isVoid || isRefund
                               ? i18n.ls('transaction', 'pmtTxDate', {
                txDate: voidOrRefundDate ? voidOrRefundDate.toLocaleDateString('en-US') : null,
                txTime: voidOrRefundDate ? voidOrRefundDate.toLocaleTimeString('en-US').toLowerCase() : null
            } as PaymentTxDate)
                               : null;

        // e.g. $1000.25
        const amountFmt = util.formatCurrency(amount, true);

        // e.g. Mastercard or Visa (card), checking or savings (bank)
        let methodName = paymentMethod.type === 'card' || (payment && isCreditCard(payment.method))
                         ? paymentMethod.cardType
                         : i18n.ls('transaction', 'pmtAccountType', {accountType: paymentMethod.accountType} as PaymentAccountType);

        // Name of the payer, will be "your" if no benefactor, or the benefactor's full name
        const payerName = emails.payer
                          ? i18n.ls('transaction', 'pmtPayerName', {name: emails.payer.name} as PaymentPayerName)
                          : i18n.c('your');

        // Name of the case, case code, or lead's case types, e.g. 'Divorce with Children', '2021-D-0001'
        let issueName = isCaseTrust && matter
                        ? i18n.ls('transaction', 'pmtForCase', {code: matter.code} as PaymentForCase)
                        : '';

        // Notes
        const comment = transaction.notes;

        // Name for initial consults
        const consultName = new OptionHelper().getOptionValue(OptionHelper.PAID_CONSULT_NAME) || i18n.c('initialConsult');

        // The text version of the email
        let text = `re: ${clientNames.join('; ')}\n\n`;

        text += `${subject}\n\n`;

        if (isVoid) text += i18n.ls('transaction', 'pmtVoidedCharge', {
            voidOrRefundOn,
            billingName,
            amountFmt,
            payerName,
            methodName,
            methodNumber: paymentMethod.number
        } as PaymentVoidedCharge) + '\n\n';
        else if (isRefund) text += i18n.ls('transaction', 'pmtRefundCharge', {
            voidOrRefundOn,
            billingName,
            isPartial,
            amountFmt,
            payerName,
            methodName,
            methodNumber: paymentMethod.number
        } as PaymentRefundCharge) + '\n\n';
        else text += i18n.ls('transaction', 'pmtChargedOn', {
                paymentDate,
                billingName,
                amountFmt,
                payerName,
                methodName,
                methodNumber: paymentMethod.number
            } as PaymentChargedOn) + '\n\n';

        if (!isVoid && !isRefund) {
            if (isTrustRelated) text += i18n.ls('transaction', 'pmtDeposited', {
                trustName, issueName
            } as PaymentDeposited) + '\n\n';
            // Only the lead's paid consult paid for the consult. Every other lead payment (fees,
            // retainers) is applied to the balance, like a case payment. (OWA-957)
            else if (props.isConsultPayment && !matter) text += i18n.ls('transaction', 'pmtConsultName', {consultName} as PaymentConsultName) + '\n\n';
            else text += i18n.ls('transaction', 'pmtAppliedTo') + '\n\n';
        }

        if (comment) text += i18n.ls('transaction', 'pmtNotes', {
            billingName,
            comment
        } as PaymentNotes) + '\n\n';

        text += i18n.ls('transaction', 'pmtThankYouBusiness');

        // The html version of the email
        let html = `<p>re: ${clientNames.join('; ')}</p><br/>`;

        html += i18n.ls('transaction', 'pmtHtmlSubject', {subject} as PaymentHtmlSubject);

        if (isVoid) html += `<p>${i18n.ls('transaction', 'pmtVoidedCharge', {
            voidOrRefundOn,
            billingName,
            amountFmt,
            payerName,
            methodName,
            methodNumber: paymentMethod.number
        } as PaymentVoidedCharge)}</p>`;
        else if (isRefund) html += `<p>${i18n.ls('transaction', 'pmtRefundCharge', {
            voidOrRefundOn,
            billingName,
            isPartial,
            amountFmt,
            payerName,
            methodName,
            methodNumber: paymentMethod.number
        } as PaymentRefundCharge)}</p>`;
        else html += `<p>${i18n.ls('transaction', 'pmtChargedOn', {
                paymentDate,
                billingName,
                amountFmt,
                payerName,
                methodName,
                methodNumber: paymentMethod.number
            } as PaymentChargedOn)}</p>`;

        if (!isVoid && !isRefund) {
            if (isTrustRelated) html += `<p>${i18n.ls('transaction', 'pmtDeposited', {
                trustName, issueName
            } as PaymentDeposited)}</p>`;
            else if (props.isConsultPayment && !matter) html += `<p>${i18n.ls('transaction', 'pmtConsultName', {consultName} as PaymentConsultName)}</p>`;
            else html += `<p>${i18n.ls('transaction', 'pmtAppliedTo')}</p>`;
        }

        if (comment) html += i18n.ls('transaction', 'pmtNotes', {
            billingName,
            comment
        } as PaymentNotes);

        html += `<br/><p>${i18n.ls('transaction', 'pmtThankYouBusiness')}</p>`;

        const to = emails.clients.map(e => e.email);
        if (emails.payer?.email) to.push(emails.payer.email);
        const copy = receiptFirmCopy();

        try {
            const options: EmailOptions = {
                from: 'payments@outlawpayments.com',
                to, // Clients and payers
                cc: copy.cc, // The firm, as its email copies setting says
                bcc: copy.bcc,
                subject,
                text,
                html,
                replyTo: copy.replyTo || 'no-reply@outlawpractice.com',
            };

            const parsedOptions = ZEmailOptions.safeParse(options);
            if (parsedOptions.error) console.error('Error parsing email options', parsedOptions);

            const emailResult = await sendEmails(options);
            if (!emailResult.isOk()) Alert.addSimpleError(`Error sending email to client or payer: ${emailResult.unwrapErr().message}`);
        }
        catch (err) {
            console.error('Error sending email to client', err);

            Alert.addSimpleError(`Error sending email to client or payer: ${err.message}`);
        }


        return text;
    }

    type SyncProcessingCallback = () => void;
    type AsyncProcessingCallback = () => void | Promise<void>;
    type SetProcessingCallback = SyncProcessingCallback | AsyncProcessingCallback;

    const isProcessing = () => sessionStorage.getItem('processingPayment') === 'true';

    /**
     * OWA-1454: what a failed portal charge means for its attempt. A refusal settles it, and so does a
     * 403 or 404 on its first send (the server answers those before sending). Every other answer keeps
     * the attempt, so the next try sends the same request and the server answers from its record
     * instead of charging again. A replay that fails the same way can only be sorted out by the firm.
     *
     * @param {Error} err - the charge call's error
     * @param {PortalChargeAttempt} attempt - the attempt that was sent
     * @param {boolean} firstSend - whether this was the attempt's first send (no earlier answer was unknown)
     */
    const onPortalChargeFailed = (err: Error, attempt: PortalChargeAttempt, firstSend: boolean, matterId: string, txType: string) => {
        const status = (err as Error & {status?: number}).status;
        if (err instanceof PortalChargeRefusedError) {
            forgetPortalCharge(matterId, txType, attempt.transactionId);
            setErrMessage(portalChargeRefusedMessage());
            // A processor decline saved a refused row under the attempt's transactionId
            void props.onRefreshPortalTransaction(attempt.transactionId).catch(console.error);
            props.onRefused?.();
        }
        else if (err instanceof PortalChargeOperatorError) {
            rememberPortalCharge(matterId, txType, {...attempt, operatorRequired: true});
            setErrMessage(portalChargeOperatorMessage());
        }
        else if (firstSend && (status === 403 || status === 404)) {
            forgetPortalCharge(matterId, txType, attempt.transactionId);
            setErrMessage(err.message);
        }
        // A 401 may come after the send (a processor 401), so the attempt stays
        else if (status === 401) setErrMessage(portalChargeSignInMessage());
        else if (chargeOutcomeUnknown(status) || status === 409) setErrMessage(portalChargeUnknownMessage());
        else setErrMessage(portalChargeStuckMessage(err.message));
    };

    /**
     * OWA-1454: one portal charge attempt for this Case and transaction type, run while holding its
     * cross-tab lock. An unsettled attempt is sent again exactly as first sent, whatever the form now
     * says; a new one is stored before it is sent, and nothing is sent when it cannot be stored.
     */
    const chargePortal = async (matterId: string, txType: string) => {
        const pending = pendingPortalCharge(matterId, txType);
        const attempt: PortalChargeAttempt = pending ?? {
            transactionId: props.transaction.id,
            request: {
                paymentMethodId: props.transaction.paymentMethod,
                amount: props.transaction.amount,
                accountId: props.accountId,
                transaction: JSON.parse(JSON.stringify(props.transaction)),
            },
        };
        if (!pending && !rememberPortalCharge(matterId, txType, attempt)) {
            setErrOccurred(true);
            setErrMessage(portalChargeNoRecordMessage());
            return;
        }
        const {paymentMethodId, amount, accountId, transaction} = attempt.request;
        const paymentResult = await paymentApi.createPayment(paymentMethodId, amount, accountId, transaction);
        if (paymentResult.isErr()) {
            const err = paymentResult.unwrapErr();
            console.warn('💳 createPayment failed', err);
            setErrOccurred(true);
            onPortalChargeFailed(err, attempt, !pending, matterId, txType);
        }
        else onCharged(paymentResult.unwrap(), () => forgetPortalCharge(matterId, txType, attempt.transactionId));
    };

    /**
     * The processor accepted the charge. For a portal charge the payments service also saved its
     * ledger row: adopt it, then settle the attempt, but only once the row is in the store and the
     * payment page has taken it. A failure before that keeps the attempt, so the next try sends the
     * same charge and the server answers with this same row.
     *
     * @param {PayrixTransactionFull} txn - the processor's answer
     * @param {Function} [settle] - settles a portal charge's attempt
     */
    const onCharged = (txn: PayrixTransactionFull, settle?: () => void) => {
        // Tell the app we finished processing with no errors
        setErrOccurred(false);
        setErrMessage('');
        setTxn(txn);

        let saved: Transaction | undefined;
        try {
            if (txn.outlawTransaction) saved = props.onAdoptPortalTransaction(txn.outlawTransaction);
        }
        catch (err) {
            console.error('Unable to adopt the saved portal payment:', err);
        }

        // Parse the payment metadata. Could throw an error!
        let cMeta: ProcessorPaymentMeta;
        try {
            cMeta = paymentApi.parsePaymentMeta(txn, props.transaction);
        }
        catch (err) {
            console.error('Unable to parse payment meta:', err);

            Alert.addSimpleError('The transaction processed successfully, but there was a problem sending emails. ' +
                'You will need to manually send an email to the client letting them know that their payment went through. ' +
                `Error: ${err.message}`
            );
            return;
        }

        // Let the payment page know. A portal page takes the saved row (which already carries the
        // results) before onProcess returns, and its failure keeps the attempt (OWA-1454).
        let taken = true;
        try {
            props.onProcess?.(cMeta, saved);
            if (!saved) props.onChange?.(cMeta);
        }
        catch (err) {
            taken = false;
            console.error('The payment page could not take the payment:', err);
            Alert.addSimpleError('The payment went through, but this page could not show it. Reload the page to see it.');
        }
        if (saved && taken) settle?.();

        // Now send the emails
        _sendEmails(cMeta)
            .catch(err => {
                Alert.addSimpleError('The transaction processed successfully, but there was a problem sending emails. ' +
                    'You will need to manually send an email to the client letting them know that their payment went through. ' +
                    `Error: ${err.message}`
                );
            });
    };

    /**
     * It is imperative that calls to onProcess always return successfully if the call to the paymentsApi return
     * successfully.  In other words, errors that occur processing the response from the API should NEVER stop
     * this function from returning a positive response. Otherwise, the UI gets out of sync with the payment, and
     * the user will be unable to save a transaction that processed successfully on the server!
     */
    const onProcess = async () => {
        setErrOccurred(false);
        setErrMessage('');
        // OWA-1454: a portal charge goes only through Payrix, whose payments service records it and
        // saves its ledger row; the portal cannot save a payment row for any other processor.
        const portalMatterId = isPortalUser() ? props.transaction.matterIds?.[0] : undefined;
        const txType = props.transaction.txType;
        if (isPortalUser() && !portalMatterId) {
            setErrOccurred(true);
            setErrMessage(portalChargeNoCaseMessage());
            return;
        }
        if (portalMatterId) {
            let payrix: boolean;
            try {
                payrix = paymentApi.isPayrix();
            }
            catch (err) {
                // No firm loaded yet: refuse rather than fail the click silently
                console.error(err);
                setErrOccurred(true);
                setErrMessage(portalChargeFirmNotLoadedMessage());
                return;
            }
            if (!payrix) {
                setErrOccurred(true);
                setErrMessage(portalChargeUnavailableMessage());
                return;
            }
        }
        if (portalMatterId && !portalChargeLocksAvailable()) {
            setErrOccurred(true);
            setErrMessage(portalChargeOldBrowserMessage());
            return;
        }
        await runInFlight(flightKey, async () => {
            if (portalMatterId) {
                const {ran} = await withPortalChargeLock(portalMatterId, txType, () => chargePortal(portalMatterId, txType));
                if (!ran) {
                    setErrOccurred(true);
                    setErrMessage(portalChargeBusyMessage());
                }
                return;
            }

            const {paymentMethod, amount} = props.transaction;

            // Call the server to create the payment. It will either return an Error or a PaymentTransactionFull
            const paymentResult = await paymentApi.createPayment(paymentMethod, amount, props.accountId, props.transaction);
            if (paymentResult.isErr()) {
                const err = paymentResult.unwrapErr();

                console.warn('💳 createPayment failed', err);

                // Spit out the error for logging purposes
                console.error(err);

                // Set the error in state
                setErrOccurred(true);
                if (err instanceof PaymentOutcomeUnknownError) {
                    // The charge may have gone through, so it is not recorded as refused.
                    setErrMessage(chargeUnknownMessage());
                }
                else if (err instanceof PaymentNotSentError) {
                    // Refused before it reached the processor, so the transaction is not refused.
                    setErrMessage(err.message);
                }
                else {
                    setErrMessage(err.message);
                    // Refused by the server
                    props.onRefused?.();
                }
            }
            else onCharged(paymentResult.unwrap());
        }).catch(err => {
            // The call itself failed (it threw instead of returning an error result), so whether the
            // charge went through is unknown: it is not recorded as refused.
            console.error(err);
            setErrOccurred(true);
            setErrMessage(portalMatterId ? portalChargeUnknownMessage() : chargeUnknownMessage());
        });
    }

    const onRefund = () => {
        void runInFlight(flightKey, () => {
            const paymentMeta = normalizePaymentMeta(props.transaction.processorPaymentMeta);
            const amount = paymentMeta?.amount || props.transaction.amount;
            // OWA-1187: a retry of this refund sends the same refund key, so the server never refunds twice.
            const submission: RefundSubmission = {firmId: getCurrentFirmId(), action: 'refund', transactionId: paymentMeta?.id, amount};

            return paymentApi.refundTransaction(paymentMeta?.id, amount, refundKeyFor(submission))
                .then(txn => {
                    refundSucceeded(submission);
                    try {
                        // The refund itself: its amount and date are what the emails report.
                        const cMeta = paymentApi.parsePaymentMeta(txn, props.transaction);

                        if (paymentApi.isPayrix()) {
                            // OWA-978: a Payrix refund is its own transaction, so it is never stored over
                            // the charge. Record the refund date now; the refresh then re-reads the charge,
                            // whose stored metadata carries the refunded total.
                            if (!props.transaction.voidedDate) {
                                const tr = props.transaction.clone();
                                tr.voidedDate = cMeta.voidOrRefundDate ?? new Date();
                                props.onSaveTransactionQueue(tr);
                            }
                            props.onRefunded?.(normalizePaymentMeta(props.transaction.processorPaymentMeta)); // Can not throw errors
                            void txnQuery.refetch();
                        }
                        else {
                            if (props.onChange) props.onChange(cMeta); // Can not throw errors
                            props.onRefunded?.(cMeta); // Can not throw errors
                        }

                        setErrOccurred(false);
                        setErrMessage('');

                        // Now send the emails
                        _sendEmails(cMeta)
                            .catch(err => {
                                console.error('Unable to parse payment meta:', err);
                                Alert.addSimpleError('The refund processed successfully, but there was a problem sending emails. ' +
                                    'You will need to manually send an email to the client letting them know that the refund went through. ' +
                                    `Error: ${err.message}`
                                );
                            })
                    }
                    catch (err) {
                        console.error('Unable to parse payment meta:', err);
                        Alert.addSimpleError('The refund processed successfully, but there was a problem sending emails. ' +
                            'You will need to manually send an email to the client letting them know that the refund went through. ' +
                            `Error: ${err.message}`
                        );
                    }
                })
                .catch(err => {
                    console.error(err);
                    setErrOccurred(true);
                    setErrMessage(err.message);
                });
        }).catch(err => {
            console.error(err);
            setErrOccurred(true);
            setErrMessage(err.message);
        });
    }

    const onVoid = () => {
        void runInFlight(flightKey, () => {
            // Normalize the processor meta so we use the canonical id field
            // regardless of whether the meta is in Payrix or LawPay shape.
            const paymentMeta = normalizePaymentMeta(props.transaction.processorPaymentMeta);
            // A void on a processed batch is a refund on the server, so it carries a refund key too.
            const submission: RefundSubmission = {firmId: getCurrentFirmId(), action: 'void', transactionId: paymentMeta?.id};
            return paymentApi.voidTransaction(paymentMeta?.id, refundKeyFor(submission))
                .then(txn => {
                    refundSucceeded(submission);
                    try {
                        const cMeta = paymentApi.parsePaymentMeta(txn, props.transaction);
                        if (props.onChange) props.onChange(cMeta);
                        setErrOccurred(false);
                        setErrMessage('');

                        // If we voided a payment, call onVoided
                        props.onVoided?.(cMeta);

                        // If we voided a refund, reinstate
                        // props.onRestored?.(cMeta);

                        // Now send the emails
                        _sendEmails(cMeta)
                            .catch(err => {
                                console.error('Unable to parse payment meta:', err);
                                Alert.addSimpleError('The payment was successfully voided, but there was a problem sending emails. ' +
                                    'You will need to manually send an email to the client letting them know that the payment was voided. ' +
                                    `Error: ${err.message}`
                                );
                            })
                    }
                    catch (err) {
                        console.error('Unable to parse payment meta:', err);
                        Alert.addSimpleError('The payment was successfully voided, but there was a problem sending emails. ' +
                            'You will need to manually send an email to the client letting them know that the payment was voided. ' +
                            `Error: ${err.message}`
                        );
                    }
                })
                .catch(err => {
                    console.error(err);
                    setErrOccurred(true);
                    setErrMessage(err.clientMessage || err.message);
                });
        }).catch(err => {
            console.error(err);
            setErrOccurred(true);
            setErrMessage(err.clientMessage || err.message);
        });
    }

    const img = paymentApi.getIcon();
    const tr = props.transaction;
    let authCode: string | null;

    if (tr) {
        authCode = normalizePaymentMeta(tr.processorPaymentMeta)?.authorization ?? null;
    }

    const hasTxn = !!props.transaction.processorPaymentMeta?.id;
    if (hasTxn && txnQuery.isLoading) return <Loading message="Loading transaction"/>;
    if (hasTxn && txnQuery.isError || txnQuery.isLoadingError)
        return <div className='u-errorOccurred'>{txnQuery.error.toString()}</div>;

    // We have to consider the combination of status and type when determining what is going on with transactions.
    // This is vendor-specific, so we call the paymentApi for help.
    // For LawPay transactions, txnQuery.data is null (API no longer exists), so derive flags from stored metadata.
    let flags: string[], message: string;
    if (hasTxn && isLawpayFormat(tr.processorPaymentMeta)) {
        const lpMeta = normalizePaymentMeta(tr.processorPaymentMeta);
        if (lpMeta) {
            const isRefund = [ETransactionType.ECheckRefund, ETransactionType.CreditCardRefund].includes(lpMeta.type);
            const isVoided = lpMeta.statusText === ETransactionStatusName.Voided;
            const amt = util.formatCurrency(lpMeta.amount, true);
            if (isRefund) {
                flags = ['REFUND', 'COMPLETE'];
                message = `Refund of ${amt} was processed via LawPay.`;
            } else if (isVoided) {
                flags = ['VOID', 'COMPLETE'];
                message = `Payment of ${amt} was voided via LawPay.`;
            } else {
                flags = ['CHARGE', 'COMPLETE'];
                message = `Payment of ${amt} was processed via LawPay.`;
            }
        } else {
            flags = [];
            message = '';
        }
    } else {
        ({flags, message} = hasTxn ? paymentApi.getCurrentStatus(!txnQuery.isLoading && txnQuery.data) : {flags: [], message: ''});
    }
    const hasFlags = flags.length > 0;
    const canVoid = flags.includes('INCOMPLETE') && !flags.includes('REFUND') && !flags.includes('REFUNDED');
    const canRefund = flags.includes('COMPLETE') && flags.includes('CHARGE');
    const canCharge = flags.length === 0;
    const emailInfo = getEmailInfo(true);
    const emails = Object.keys(emailInfo).map(k => {
        const o = emailInfo[k];

        if (Array.isArray(o)) return o.map(obj => `${obj.email} (client)`);
        else return `${o.email} (${k})`;

    }).flat().filter(util.existsFilter);
    const receiptCopy = receiptFirmCopy();
    emails.push(...receiptCopy.cc.map(e => `${e} (firm)`), ...receiptCopy.bcc.map(e => `${e} (firm, BCC)`));
    const hasEmails = emails.length > 0;
    const meta = normalizePaymentMeta(tr.processorPaymentMeta);
    // OWA-1454: Process sends the unsettled attempt for this Case, so say what it will do
    const portalMatterId = isPortalUser() && canCharge ? tr.matterIds?.[0] : undefined;
    const unsettled = portalMatterId ? pendingPortalCharge(portalMatterId, tr.txType) : null;

    return (
        <div className='payment-action-box'>
            <div className='fieldset'>
                {/* Processor Logo */}
                <h1>
                    {/* Processor Logo */}
                    {/* @ts-ignore */}
                    <img src={img} height={32} alt={paymentApi.getName()}/>

                    {/* Let them know where the emails will be sent (if any), and a link to the config */}
                    {!isPortalUser() && <RoutedIconButton type={Icons.CONFIG}
					                                      title={i18n.ls('transaction', 'pmtConfigurePayments')}
					                                      label={i18n.ls('transaction', 'pmtConfigure')}
					                                      route='/config/financial/banking'
					/>}
                </h1>

                <div className='payment-action-box__content'>

                    {/* Status */}
                    <div className='payment-action-box__status'>
                        {/* Status Message */}
                        {message && <div style={{marginTop: '1em', fontWeight: 600}}>{message}</div>}

                        {/* An unsettled portal charge (OWA-1454) */}
                        {unsettled && !errOccurred && !isTxnProcessing && <div className='payment-action-box__unsettled' style={{marginTop: '1em'}}>
                            {unsettled.operatorRequired
                             ? portalChargeOperatorMessage()
                             : `An earlier payment of ${util.formatCurrency(unsettled.request.amount, true)} has not been confirmed yet. ` +
                               'Pay checks that payment again and does not charge you twice.'}
						</div>}

                        {/* Type */}
                        {!isPortalUser() && meta?.typeText && meta.voidOrRefundDate &&
							<div style={{marginTop: '1em'}}>
								<label style={{fontWeight: 600}}>{meta.typeText}</label> on {meta.voidOrRefundDate.toLocaleString('en-US')}
							</div>
                        }

                        {/* Emails */}
                        {hasEmails && <div style={{marginTop: '5px'}}>
							<div style={{
                                marginTop: '1em',
                                fontWeight: 600
                            }}>{i18n.ls('transaction', txn ? 'pmtEmailsSent' : 'pmtEmails')}</div>
							<ul>
                                {emails.map(e => <li key={e}>{e}</li>)}
							</ul>
						</div>
                        }

                        {/* Who ran the payment? */}
                        {!isPortalUser() && userProcessor && <div style={{marginTop: '1em'}}>
							<label style={{fontWeight: '600', marginRight: '5px'}}>
                                {i18n.ls('transaction', 'pmtProcessorPaymentUser')}
							</label>
							<span>{userProcessor || ''}</span>
						</div>}

                        {/* Authorization Code (if available) */}
                        {hasFlags && authCode && <div style={{marginTop: '1em'}}>
							<CopyText label={i18n.ls('transaction', 'pmtAuthCode')}
							          text={authCode}
							          useCodeStyle={true}
							/>
						</div>
                        }

                        {/* Payment ID */}
                        {!isPortalUser() && meta?.id && <div style={{marginTop: '1em'}}>
							<CopyText text={meta?.id}
							          label={i18n.ls('transaction', 'pmtPaymentId')}
							          useCodeStyle={true}
							/>
						</div>}

                        {/* Show the transaction results */}
                        {txn && <ul>
                            {txn.txnResults?.map((txnResults) => {
                                return <li key={txnResults.id}>
                                    {txnResults.created} [{TxnResultTypes[txnResults.code]}] [{txnResults.bankCode}] {txnResults.message}
                                </li>
                            })}
						</ul>
                        }

                    </div>

                    {/* Action Buttons */}
                    <div className='payment-action-box__buttons'>
                        {/* Process Button */}
                        {!meta?.chargeDate && canCharge && tr.amount && tr.paymentMethod &&
							<IconButton type={Icons.CHARGE}
							            label={i18n.ls('transaction', isPortalUser()
                                                                      ? 'pmtProcess_portal'
                                                                      : 'pmtProcess'
                                        )}
							            labelBold={true}
							            onClick={onProcess}
							            disabled={isTxnProcessing}
							            color='darkgreen'
							            textColor='darkgreen'
							/>
                        }

                        {/* Void Button */}
                        {!isPortalUser() && canVoid &&
							<IconButton type={Icons.VOID}
							            label={i18n.ls('transaction', 'pmtVoid')}
							            labelBold={true}
							            onClick={onVoid}
							            disabled={isTxnProcessing}
							            textColor='maroon'
							/>}

                        {/* Refund Button */}
                        {!isPortalUser() && canRefund &&
							<IconButton type={Icons.REFUND}
							            label={i18n.ls('transaction', 'pmtRefund')}
							            labelBold={true}
							            color='maroon'
							            onClick={onRefund}
							            disabled={isTxnProcessing}
							            textColor='maroon'
							/>}
                    </div>

                    {/* Error messages */}
                    {errOccurred && <div className='payment-action-box__error'>
                        {errMessage || ''}
					</div>
                    }

                </div>
            </div>

        </div>
    );
});

PaymentActionBox.displayName = 'PaymentActionBox';

const mapStateToProps = (state: RootState) => {
    return {
        resources: state.resources,
        transactions: state.transactions,
    };
};

const mapDispatchToProps = (dispatch: RootDispatch) => {
    return {
        onSaveTransactionQueue: tr => dispatch(TransactionActions.saveTransactionQueue(tr)),
        onAdoptPortalTransaction: (row: Record<string, unknown>) => adoptPortalTransaction(dispatch, row),
        onRefreshPortalTransaction: (transactionId: string) => refreshPortalTransaction(dispatch, transactionId),
    };
};

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(PaymentActionBox);
